import { Button, Card, CardContent, SwipeableDrawer } from '@material-ui/core';
import { useMutation, useQueryClient } from 'react-query';
import { AddCircleOutline } from '@material-ui/icons';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useImmer } from 'use-immer';
import toast from 'react-hot-toast';
import Modal from 'react-modal';

import { AddMunicipalityBlackListProps, Guid, PaginatedNameFilterOptions } from '@project/types';
import {
  AddMunicipalityBlackListForm,
  BasicTable,
  PaginationButtons,
  SearchField,
} from '@project/components';
import { useMunicipalityBlackListData } from '@project/react-queries';
import { municipalityBlackListApiService } from '@project/services';

Modal.setAppElement('#root');

export const MunicipalityBlackListScreen: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const history = useHistory();
  const queryClient = useQueryClient();
  const _handleSuccess = () => {
    queryClient.invalidateQueries(['municipality-black-list']);
    setTimeout(function waitForToast() {
      history.push('/admin/municipality-black-list');
    }, 2500);
  };

  const _addMunicipality = (formData: AddMunicipalityBlackListProps) => {
    return municipalityBlackListApiService.addMunicipalityBlackList(formData);
  };

  const _deleteData = useMutation(
    'deleteMunicipalityBlackList',
    (id: Guid) => municipalityBlackListApiService.removeMunicipalityBlackList(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['municipality-black-list']);
        toast.success('Successfully removed municipality');
      },
      onError: () => {
        toast.error('Error removing municipality');
      },
    },
  );

  const _addMunicipalityFailed = () => toast.error('Error adding municipality to black list');

  const [blackListFilterState, setBlackListFilterState] = useImmer<PaginatedNameFilterOptions>({
    pageNum: 1,
    pageEntries: 10,
    name: '',
  });

  const setPage = (pageNumber: number, pageEntries: number) => {
    setBlackListFilterState({
      ...blackListFilterState,
      pageNum: pageNumber,
      pageEntries,
    });
  };

  const blackListQuery = useMunicipalityBlackListData(blackListFilterState);
  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'municipalityName',
      },
    ],
    [],
  );

  return (
    <>
      <div className="flex flex-row justify-between">
        <div className="mt-2 flex justify-start mx-8 text-xl">Municipality Black List</div>
        <Button
          className="mt-2 flex bg-tertiary text-white hover:bg-tertiary-dark mr-8"
          size="small"
          startIcon={<AddCircleOutline />}
          onClick={() => setDrawerOpen(true)}
        >
          Add New
        </Button>
      </div>
      <div className="border-b border-black mt-2 flex justify-start mx-8" />
      <div className="flex justify-start mx-8 mt-2 space-x-2">
        <SearchField
          placeholder="Name"
          value={blackListFilterState.name}
          onChange={(value) => {
            setBlackListFilterState({
              ...blackListFilterState,
              name: value,
              pageNum: 1,
            });
          }}
          isLoading={blackListQuery.isFetching}
        />
      </div>
      {!blackListQuery.isLoading && (
        <>
          <div className="flex flex-col flex-grow mx-8 mt-2">
            <Card className="flex flex-grow shadow-2xl">
              <CardContent className="flex flex-col flex-grow p">
                <BasicTable
                  tableData={blackListQuery.data.items}
                  tableColumns={columns}
                  path=""
                  isViewable={false}
                  isDeletable
                  onDelete={({ id }) => _deleteData.mutateAsync(id)}
                />
              </CardContent>
            </Card>
          </div>
          <PaginationButtons
            page={blackListFilterState.pageNum}
            setPage={setPage}
            pageIndex={blackListQuery.data.pageIndex}
            totalPages={blackListQuery.data.totalPages}
            pageEntries={blackListFilterState.pageEntries}
          />
        </>
      )}
      <SwipeableDrawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
      >
        <AddMunicipalityBlackListForm
          initialValues={{
            municipalities: [],
          }}
          submitForm={_addMunicipality}
          onSuccess={() => {
            _handleSuccess();
            setDrawerOpen(false);
          }}
          onFailure={_addMunicipalityFailed}
        />
      </SwipeableDrawer>
    </>
  );
};
